import { Component } from '@theme/component';

class CurateYourSet extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.handleClick);

    // After the main product is added to cart, also add curate items
    document.addEventListener('cart:bundled-add', this.handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:bundled-add', this.handleCartAdd);
  }

  /** @param {MouseEvent} e */
  handleClick = (e) => {
    const target = /** @type {HTMLElement | null} */ (e.target);
    const btn = target?.closest('[data-action]');
    if (!(btn instanceof HTMLElement)) return;

    const action = btn.dataset.action;
    if (!action) return;

    switch (action) {
      case 'increase':
      case 'decrease':
        this.updateItemQty(btn, action);
        break;
      case 'select-all':
        this.selectAll();
        break;
    }
  };

  /**
   * @param {HTMLElement} btn
   * @param {'increase' | 'decrease'} action
   */
  updateItemQty(btn, action) {
    const item = btn.closest('.curate-set__item');
    if (!item) return;

    const valueEl = item.querySelector('.curate-set__qty-value');
    if (!valueEl) return;

    let qty = parseInt(valueEl.textContent ?? '', 10) || 0;
    qty = action === 'increase' ? qty + 1 : Math.max(0, qty - 1);
    valueEl.textContent = String(qty);

    const decreaseBtn = /** @type {HTMLButtonElement | null} */ (
      item.querySelector('[data-action="decrease"]')
    );
    if (decreaseBtn) decreaseBtn.disabled = qty === 0;
  }

  selectAll() {
    // Set qty to 1 for all available items, open the dropdown
    this.querySelectorAll('.curate-set__item[data-available="true"]').forEach((item) => {
      const valueEl = item.querySelector('.curate-set__qty-value');
      if (valueEl) valueEl.textContent = '1';

      const decreaseBtn = /** @type {HTMLButtonElement | null} */ (
        item.querySelector('[data-action="decrease"]')
      );
      if (decreaseBtn) decreaseBtn.disabled = false;
    });

    // Open disclosure if closed
    const trigger = /** @type {HTMLElement | null} */ (this.querySelector('[ref="disclosureTrigger"]'));
    if (trigger && trigger.getAttribute('aria-expanded') !== 'true') {
      trigger.click();
    }
  }

  /**
   * Collects curate items with qty > 0 and returns them as an array
   * ready for /cart/add.js
   * @returns {{ id: number, quantity: number }[]}
   */
  getCurateItems() {
    /** @type {{ id: number, quantity: number }[]} */
    const items = [];
    this.querySelectorAll('.curate-set__item[data-available="true"]').forEach((item) => {
      const htmlItem = /** @type {HTMLElement} */ (item);
      const qty = parseInt(htmlItem.querySelector('.curate-set__qty-value')?.textContent ?? '', 10) || 0;
      const variantId = htmlItem.dataset.variantId;
      if (qty > 0 && variantId) {
        items.push({
          id: parseInt(variantId, 10),
          quantity: qty,
        });
      }
    });
    return items;
  }

  /**
   * Listener for product-form's `cart:bundled-add` event.
   *
   * The event carries:
   *   detail.sectionIds: string[]   — section IDs to render in the cart response
   *   detail.waitFor:    Promise[]  — listeners push their fetch promise here
   *                                   so the dispatcher can await them and use
   *                                   the latest sections HTML for the cart UI
   *                                   morph (avoids race where the cart drawer
   *                                   refreshes before bundled items are added).
   *
   * @param {Event} event
   */
  handleCartAdd = (event) => {
    const items = this.getCurateItems();
    if (items.length === 0) return;

    const cartUrl = this.dataset.cartUrl;
    if (!cartUrl) return;

    const detail = /** @type {{ sectionIds?: string[], waitFor?: Promise<any>[] }} */ (
      /** @type {CustomEvent} */ (event).detail ?? {}
    );
    const sectionIds = detail.sectionIds ?? [];

    /** @type {Record<string, unknown>} */
    const body = { items };
    if (sectionIds.length > 0) body.sections = sectionIds.join(',');

    const promise = fetch(cartUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          // Surface the error to console — user already saw the main item
          // added, so we don't want to throw a UI error, but devs need to know.
          console.error('CurateYourSet: cart add failed', response.status, errorData);
          return null;
        }
        return await response.json();
      })
      .catch((e) => {
        console.error('CurateYourSet: cart add error', e);
        return null;
      });

    /* Push into waitFor so product-form awaits us before dispatching
       CartAddEvent. Without this, the cart drawer renders stale HTML
       that doesn't include our bundled items. */
    if (Array.isArray(detail.waitFor)) {
      detail.waitFor.push(promise);
    }
  };
}

if (!customElements.get('curate-your-set')) {
  customElements.define('curate-your-set', CurateYourSet);
}
